import { useQueryClient } from '@tanstack/react-query';
import { useBranchesStore } from '@/shared/store/branchesStore';
import { useAuthStore } from '@/shared/store/authStore';
import { isBranchKeyedListQuery } from '@/shared/hooks/useServerList';
import type { DropdownItem } from '@/components/ui/DropdownMenu';

// The branch filter's menu: the top-bar BranchPicker on desktop, the Settings
// menu on phones (the pill is hidden below md). Picking writes
// branchesStore.currentBranchId (sent as X-Branch-Id on every request) and
// refetches every cached query, which was fetched under the old branch. The
// server lists name the branch in their keys and refetch on the re-render: an
// active refetch now would still run under their old keys, sending the new
// branch and caching its rows under the old one. They are only marked stale.
export function useBranchMenu(): { visible: boolean; label: string; title: string; items: DropdownItem[] } {
  const branches = useBranchesStore((s) => s.branches);
  const current = useBranchesStore((s) => s.currentBranchId);
  const setCurrent = useBranchesStore((s) => s.setCurrent);
  const userId = useAuthStore((s) => s.user?.id);
  const qc = useQueryClient();
  const pick = (id: string | null) => {
    setCurrent(id, userId);
    void qc.invalidateQueries({ predicate: (q) => !isBranchKeyedListQuery(q.queryKey) });
    void qc.invalidateQueries({ predicate: (q) => isBranchKeyedListQuery(q.queryKey), refetchType: 'none' });
  };
  const sel = current ? branches.find((b) => b.id === current) : null;
  return {
    // Fewer than 2 active branches: nothing to pick.
    visible: branches.length >= 2,
    label: sel ? sel.code : 'All',
    title: sel ? `Active branch: ${sel.name}` : 'Showing all branches you can see',
    items: [
      { label: 'All branches', onClick: () => pick(null), divider: true, active: !sel },
      ...branches.map((b) => ({ label: `${b.code} — ${b.name}`, onClick: () => pick(b.id), active: sel?.id === b.id })),
    ],
  };
}
